<template>
  <div class="policysales-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">{{ $t('policySales.title') }}</h1>
      </div>
    </div>

    <!-- SUMMARY CARDS -->
    <v-row class="mb-2">
      <v-col cols="12" md="3">
        <div class="summary-card">
          <div class="summary-label">{{ $t('policySales.summaryTotal') }}</div>
          <div class="summary-value accent-blue">{{ formatAmount(summary.totalAmountGr) }}</div>
          <div class="summary-sub">{{ $t('policySales.policiesCount') }}: {{ summary.totalCount }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="3">
        <div class="summary-card">
          <div class="summary-label">{{ $t('policySales.summaryPaid') }}</div>
          <div class="summary-value accent-green">{{ formatAmount(summary.paidAmountGr) }}</div>
          <div class="summary-sub">{{ $t('policySales.policiesCount') }}: {{ summary.paidCount }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="3">
        <div class="summary-card">
          <div class="summary-label">{{ $t('policySales.summaryUnpaid') }}</div>
          <div class="summary-value accent-orange">{{ formatAmount(summary.unpaidAmountGr) }}</div>
          <div class="summary-sub">{{ $t('policySales.policiesCount') }}: {{ summary.unpaidCount }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="3">
        <div class="summary-card">
          <div class="summary-label">Prowizja BTP</div>
          <div class="summary-value accent-purple">{{ formatAmount(summary.paidCommissionGr) }}</div>
          <div class="summary-sub">należna z opłaconych · oczekiwana {{ formatAmount(summary.unpaidCommissionGr) }}</div>
          <div v-if="summary.missingCommissionCount" class="summary-warn">bez stawki prowizji: {{ summary.missingCommissionCount }}</div>
        </div>
      </v-col>
    </v-row>

    <!-- ==================== OFERTY ==================== -->
    <v-card outlined class="mb-4" style="background:#232838;border-color:rgba(255,255,255,.06);border-radius:18px">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-shield-search</v-icon>
        Oferty ubezpieczeń
        <v-spacer />
        <v-btn small outlined class="mr-2" @click="openCommissionTable">
          <v-icon small left>mdi-percent-outline</v-icon>Tabela prowizji
        </v-btn>
        <v-btn small color="primary" @click="openOfferDialog(null)">
          <v-icon small left>mdi-plus</v-icon>Nowa oferta
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="offerHeaders"
        :items="offers"
        :loading="offersLoading"
        :items-per-page="10"
        class="elevation-0"
        dark
      >
        <template v-slot:[`item.target`]="{ item }">
          <div>
            <b>{{ offerTargetLabel(item) }}</b>
            <div class="text-caption grey--text">{{ offerVehicleLabel(item) }}</div>
          </div>
        </template>
        <template v-slot:[`item.types`]="{ item }">
          <v-chip v-for="ty in item.types" :key="ty" x-small class="mr-1">{{ ty }}</v-chip>
        </template>
        <template v-slot:[`item.variants`]="{ item }">
          <div v-for="v in item.variants" :key="v.id" class="text-caption"
            :class="{ 'green--text font-weight-bold': item.chosenVariantId === v.id }">
            {{ v.insurer }} — {{ formatAmount(v.premiumGr) }}
            <span v-if="item.chosenVariantId === v.id">✓ wybrany</span>
          </div>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip x-small :color="offerStatusColor(item.status)" dark>{{ offerStatusLabel(item.status) }}</v-chip>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn v-if="['DRAFT','SENT'].includes(item.status)" icon small title="Edytuj" @click="openOfferDialog(item)">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn v-if="item.status === 'DRAFT' && item.targetType !== 'FLEET'" icon small color="info" title="Wyślij do odbiorcy (SMS + e-mail)" @click="sendOffer(item)">
            <v-icon small>mdi-send</v-icon>
          </v-btn>
          <v-btn v-if="['DRAFT','SENT'].includes(item.status)" icon small color="amber" title="Wybierz wariant (biuro)" @click="openChooseDialog(item)">
            <v-icon small>mdi-check-decagram-outline</v-icon>
          </v-btn>
          <v-btn v-if="item.status === 'CHOSEN'" icon small color="success" title="Wystaw polisę" @click="openIssueDialog(item)">
            <v-icon small>mdi-file-certificate-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- OFFER DIALOG -->
    <v-dialog content-class="dark-form-dialog" v-model="offerDialog" max-width="820px">
      <v-card>
        <v-card-title>{{ offerForm.id ? 'Edytuj ofertę' : 'Nowa oferta ubezpieczenia' }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-select v-model="offerForm.targetType" :items="offerTargetOptions" label="Odbiorca" outlined dense />
            </v-col>
            <v-col cols="12" md="8" v-if="offerForm.targetType !== 'FLEET'">
              <v-autocomplete
                v-model="offerForm.driverId"
                :items="driverOptions"
                :search-input.sync="driverSearch"
                item-text="text" item-value="id"
                label="Kierowca / właściciel podfloty" outlined dense
                no-filter hide-no-data placeholder="wpisz nazwisko…"
              />
            </v-col>
            <v-col cols="12" md="8" v-else>
              <v-autocomplete
                v-model="offerForm.carId"
                :items="carOptions"
                item-text="text" item-value="id"
                label="Auto floty" outlined dense
              />
            </v-col>
            <v-col cols="12" v-if="offerForm.targetType !== 'FLEET'">
              <v-text-field v-model="offerForm.vehicleDesc" label="Auto (marka, model, rejestracja)" outlined dense
                hint="dla auta spoza floty — wpisz ręcznie" persistent-hint />
            </v-col>
            <v-col cols="12" md="8">
              <div class="text-caption grey--text mb-1">Typy ubezpieczenia</div>
              <v-chip-group v-model="offerForm.types" multiple active-class="primary--text">
                <v-chip v-for="ty in ['OC','AC','NNW','ASSISTANCE']" :key="ty" :value="ty" filter outlined>{{ ty }}</v-chip>
              </v-chip-group>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="offerForm.validUntil" label="Oferta ważna do" type="date" outlined dense />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="offerForm.note" label="Notatka (widoczna dla odbiorcy)" outlined dense />
            </v-col>
          </v-row>

          <div class="d-flex align-center mb-2">
            <span class="text-subtitle-2 font-weight-bold">Warianty</span>
            <v-spacer />
            <v-btn x-small outlined @click="offerForm.variants.push({ insurer: '', scope: '', premiumZl: null, installmentsInfo: '' })">
              <v-icon x-small left>mdi-plus</v-icon>Dodaj wariant
            </v-btn>
          </div>
          <v-row v-for="(v, i) in offerForm.variants" :key="i" dense class="align-center">
            <v-col cols="12" md="3"><v-text-field v-model="v.insurer" label="Towarzystwo" outlined dense hide-details /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="v.scope" label="Zakres (co obejmuje)" outlined dense hide-details /></v-col>
            <v-col cols="6" md="2"><v-text-field v-model.number="v.premiumZl" label="Składka" suffix="zł" type="number" outlined dense hide-details /></v-col>
            <v-col cols="5" md="2"><v-text-field v-model="v.installmentsInfo" label="Raty (opis)" outlined dense hide-details /></v-col>
            <v-col cols="1" class="text-center">
              <v-btn icon small color="error" @click="offerForm.variants.splice(i, 1)"><v-icon small>mdi-close</v-icon></v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="offerDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :loading="offerSaving" @click="saveOffer">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CHOOSE DIALOG (biuro: flota / wybór telefoniczny) -->
    <v-dialog content-class="dark-form-dialog" v-model="chooseDialog.show" max-width="560px">
      <v-card v-if="chooseDialog.offer">
        <v-card-title>Wybierz wariant</v-card-title>
        <v-card-text>
          <v-radio-group v-model="chooseDialog.variantId">
            <v-radio v-for="v in chooseDialog.offer.variants" :key="v.id" :value="v.id"
              :label="`${v.insurer} — ${v.scope} — ${formatAmount(v.premiumGr)}${v.installmentsInfo ? ' (' + v.installmentsInfo + ')' : ''}`" />
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="chooseDialog.show = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :disabled="!chooseDialog.variantId" :loading="chooseDialog.saving" @click="chooseVariant">Zatwierdź wybór</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ISSUE DIALOG -->
    <v-dialog content-class="dark-form-dialog" v-model="issueDialog.show" max-width="560px">
      <v-card v-if="issueDialog.offer">
        <v-card-title>Wystaw polisę</v-card-title>
        <v-card-text>
          <div class="mb-3 grey--text">
            {{ offerTargetLabel(issueDialog.offer) }} · wybrany wariant:
            <b>{{ chosenVariantLabel(issueDialog.offer) }}</b>
          </div>
          <v-text-field v-model="issueDialog.policyNumber" label="Numer polisy" outlined dense />
          <v-row dense>
            <v-col cols="6"><v-text-field v-model="issueDialog.startDate" label="Ochrona od" type="date" outlined dense /></v-col>
            <v-col cols="6"><v-text-field v-model="issueDialog.endDate" label="Ochrona do" type="date" outlined dense /></v-col>
          </v-row>
          <div class="text-caption grey--text">
            Prowizja BTP naliczy się automatycznie wg tabeli prowizji Eurofinance (polisa nowa, produkt wg typów z oferty).
            Po wystawieniu dograj skan polisy w rejestrze poniżej (edycja pozycji).
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="issueDialog.show = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="success" :loading="issueDialog.saving"
            :disabled="!issueDialog.policyNumber || !issueDialog.startDate || !issueDialog.endDate"
            @click="issuePolicy">Wystaw</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- TABELA PROWIZJI (Eurofinance, Załącznik nr 6) -->
    <v-dialog content-class="dark-form-dialog" v-model="tableDialog.show" max-width="1280px" scrollable>
      <v-card>
        <v-card-title>
          Tabela prowizji Eurofinance
          <v-spacer />
          <span class="text-caption grey--text">stawki netto od składki · tabela z {{ commissionTable.validFrom }}</span>
        </v-card-title>
        <v-card-text>
          <v-tabs v-model="tableDialog.tab" background-color="transparent" dark class="mb-3">
            <v-tab v-for="c in categoryTabs" :key="c.value">{{ c.text }}</v-tab>
          </v-tabs>
          <div class="commission-table-wrap">
            <table v-if="tableCategory === 'ZYCIE'" class="commission-table">
              <thead>
                <tr><th>Produkt</th><th>Towarzystwo</th><th>Wariant</th><th>Stawka wg roku polisy</th></tr>
              </thead>
              <tbody>
                <tr v-for="row in tableRows" :key="row.key">
                  <td>{{ row.name }}</td>
                  <td>{{ row.insurer }}</td>
                  <td>{{ row.variant || '—' }}</td>
                  <td class="text-left">{{ row.years }}</td>
                </tr>
              </tbody>
            </table>
            <table v-else class="commission-table">
              <thead>
                <tr>
                  <th>Produkt</th>
                  <th v-for="col in tableColumns" :key="col.key">{{ col.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in tableRows" :key="row.key">
                  <td>{{ row.name }}</td>
                  <td v-for="col in tableColumns" :key="col.key" :class="cellClass(row.cells[col.key])">
                    {{ row.cells[col.key] ? row.cells[col.key].text : '' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="text-caption grey--text mt-2">
            Zakres (np. 12%–13%) liczy się wg dolnej granicy — inną stawkę wpisz ręcznie przy polisie.
            BRAK = produktu nie ma w tym towarzystwie. Prowizja należy się od zapłaconej składki.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="tableDialog.show = false">Zamknij</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- TABLE -->
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :options.sync="options"
      :server-items-length="total"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      class="elevation-1"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>{{ $t('policySales.title') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-menu v-model="monthMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="monthFilterLabel"
                :label="$t('policySales.monthFilter')"
                prepend-inner-icon="mdi-calendar-month"
                readonly
                clearable
                hide-details
                dense
                outlined
                class="mr-4"
                style="max-width:200px"
                v-bind="attrs"
                v-on="on"
                @click:clear="monthFilter = ''"
              ></v-text-field>
            </template>
            <v-date-picker v-model="monthFilter" type="month" @input="monthMenu = false"></v-date-picker>
          </v-menu>
          <v-select
            v-model="paidFilter"
            :items="paidFilterItems"
            hide-details
            dense
            outlined
            class="mr-4"
            style="max-width:200px"
          ></v-select>
          <v-text-field
            v-model="search"
            :label="$t('common.search') + '...'"
            single-line
            hide-details
            clearable
            class="mr-4"
            style="max-width:300px"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
          <v-btn color="primary" dark @click="openCreateDialog">
            <v-icon left>mdi-plus</v-icon>{{ $t('policySales.addButton') }}
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:[`item.amountGr`]="{ item }">{{ formatAmount(item.amountGr) }}</template>
      <template v-slot:[`item.insurer`]="{ item }">
        <div>{{ item.insurer || '—' }}</div>
        <div class="text-caption grey--text">{{ productLabel(item) }}</div>
      </template>
      <template v-slot:[`item.commissionGr`]="{ item }">
        <div v-if="item.commissionPercent !== null && item.commissionPercent !== undefined" :title="item.commissionNote || ''">
          <b>{{ formatAmount(item.commissionGr) }}</b>
          <div class="text-caption grey--text">{{ formatPercent(item.commissionPercent) }}<span v-if="item.commissionManual"> · ręcznie</span></div>
        </div>
        <v-chip v-else x-small color="orange darken-2" text-color="white" :title="item.commissionNote || ''">brak stawki</v-chip>
      </template>
      <template v-slot:[`item.paid`]="{ item }">
        <v-chip
          small
          :color="item.paid ? 'success' : 'orange darken-2'"
          text-color="white"
          style="cursor:pointer"
          @click="togglePaid(item)"
        >
          {{ item.paid ? $t('policySales.paid') : $t('policySales.unpaid') }}
        </v-chip>
      </template>
      <template v-slot:[`item.paidAt`]="{ item }">{{ formatDate(item.paidAt) }}</template>
      <template v-slot:[`item.createdAt`]="{ item }">{{ formatDate(item.createdAt) }}</template>
      <template v-slot:[`item.file`]="{ item }">
        <v-btn v-if="item.fileUrl" icon small color="primary" :title="item.originalName" @click="downloadFile(item)">
          <v-icon small>mdi-file-download</v-icon>
        </v-btn>
        <span v-else>—</span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn icon small color="orange" @click="openEditDialog(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
        <v-btn icon small color="red" @click="confirmDelete(item)"><v-icon small>mdi-delete</v-icon></v-btn>
      </template>
    </v-data-table>

    <!-- CREATE/EDIT DIALOG -->
    <v-dialog content-class="dark-form-dialog" v-model="formDialog" max-width="700px">
      <v-card>
        <v-card-title>{{ editMode ? $t('policySales.editPolicy') : $t('policySales.addPolicy') }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.buyerName"
                  :label="$t('policySales.buyerName') + ' *'"
                  :rules="[rules.required]"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.policyNumber"
                  :label="$t('policySales.policyNumber') + ' *'"
                  :rules="[rules.required]"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.subject"
                  :label="$t('policySales.subject') + ' *'"
                  :rules="[rules.required]"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.amount"
                  :label="$t('policySales.amount') + ' (zł) *'"
                  :rules="[rules.required, rules.amount]"
                  outlined
                  dense
                  suffix="zł"
                ></v-text-field>
              </v-col>
              <v-col cols="12"><div class="form-section-label">Prowizja — tabela Eurofinance</div></v-col>
              <v-col cols="12" md="6">
                <v-combobox
                  v-model="form.insurer"
                  :items="insurerItems"
                  label="Towarzystwo"
                  outlined
                  dense
                  clearable
                  hint="spoza listy — wpisz nazwę i podaj stawkę ręcznie"
                  persistent-hint
                ></v-combobox>
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.product"
                  :items="productItems"
                  item-text="name"
                  item-value="code"
                  label="Produkt z tabeli"
                  outlined
                  dense
                  clearable
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6" v-if="showRenewal">
                <v-radio-group v-model="form.isRenewal" row dense hide-details class="mt-0">
                  <v-radio label="Nowa polisa" :value="false"></v-radio>
                  <v-radio label="Wznowienie" :value="true"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12" md="6" v-if="selectedProduct && selectedProduct.variants.length">
                <v-select v-model="form.productVariant" :items="selectedProduct.variants" label="Wariant" outlined dense></v-select>
              </v-col>
              <v-col cols="6" md="3" v-if="selectedProduct && selectedProduct.category === 'ZYCIE'">
                <v-text-field v-model="form.policyYear" label="Rok polisy" type="number" min="1" outlined dense hint="1 = pierwszy rok" persistent-hint></v-text-field>
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field v-model="form.commissionPercent" label="Stawka ręczna" suffix="%" outlined dense clearable hint="puste = z tabeli" persistent-hint></v-text-field>
              </v-col>
              <v-col cols="12">
                <div class="commission-preview" :class="{ 'commission-preview--warn': preview.commissionPercent === null }">
                  <template v-if="preview.commissionPercent !== null">
                    Prowizja: <b>{{ formatAmount(preview.commissionGr) }}</b>
                    ({{ formatPercent(preview.commissionPercent) }}{{ preview.commissionManual ? ', stawka ręczna' : '' }})
                  </template>
                  <template v-else>Prowizja się nie policzy — brak stawki.</template>
                  <div v-if="preview.commissionNote" class="commission-preview-note">{{ preview.commissionNote }}</div>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-checkbox v-model="form.paid" :label="$t('policySales.paidCheckbox')" hide-details class="mt-1"></v-checkbox>
              </v-col>
              <v-col cols="12" md="6" v-if="form.paid">
                <v-menu v-model="paidAtMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.paidAt"
                      :label="$t('policySales.paidAt')"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="form.paidAt" @input="paidAtMenu = false"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12">
                <v-file-input
                  v-model="form.file"
                  :label="$t('policySales.attachment')"
                  accept="image/*,.pdf,.doc,.docx"
                  outlined
                  dense
                  prepend-icon=""
                  prepend-inner-icon="mdi-paperclip"
                  show-size
                ></v-file-input>
                <div v-if="editMode && editFileName" class="current-file">
                  {{ $t('policySales.currentFile') }}: <strong>{{ editFileName }}</strong>
                  <span class="current-file-hint">({{ $t('policySales.replaceFileHint') }})</span>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="formDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitForm">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");

export default {
  name: "PolicySalesPage",

  data() {
    return {
      // ——— Oferty ubezpieczeń ———
      offers: [],
      offersLoading: false,
      offerHeaders: [
        { text: 'Odbiorca', value: 'target', sortable: false },
        { text: 'Typy', value: 'types', sortable: false },
        { text: 'Warianty', value: 'variants', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Utworzono', value: 'createdAt', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
      offerTargetOptions: [
        { text: 'Kierowca (własne auto)', value: 'DRIVER' },
        { text: 'Właściciel podfloty', value: 'SUBFLEET_OWNER' },
        { text: 'Auto floty BTP', value: 'FLEET' },
      ],
      offerDialog: false,
      offerSaving: false,
      offerForm: { id: null, targetType: 'DRIVER', driverId: null, carId: null, vehicleDesc: '', types: ['OC'], validUntil: '', note: '', variants: [] },
      driverOptions: [],
      driverSearch: '',
      carOptions: [],
      chooseDialog: { show: false, offer: null, variantId: null, saving: false },
      issueDialog: { show: false, offer: null, policyNumber: '', startDate: '', endDate: '', saving: false },
      // ——— Tabela prowizji Eurofinance ———
      commissionTable: { validFrom: '', insurers: [], products: [], rates: [] },
      tableDialog: { show: false, tab: 0 },
      categoryTabs: [
        { text: 'Komunikacja', value: 'KOMUNIKACJA' },
        { text: 'Majątek', value: 'MAJATEK' },
        { text: 'Życie', value: 'ZYCIE' },
      ],
      preview: { commissionPercent: null, commissionGr: 0, commissionManual: false, commissionNote: null },
      previewTimer: null,
      loading: false,
      saving: false,
      items: [],
      total: 0,
      options: { page: 1, itemsPerPage: 10 },
      search: "",
      paidFilter: "",
      monthFilter: "",
      monthMenu: false,
      summary: {
        totalCount: 0,
        totalAmountGr: 0,
        paidCount: 0,
        paidAmountGr: 0,
        unpaidCount: 0,
        unpaidAmountGr: 0,
      },
      formDialog: false,
      formValid: false,
      editMode: false,
      editId: null,
      editFileName: null,
      paidAtMenu: false,
      form: {
        buyerName: "",
        policyNumber: "",
        subject: "",
        amount: "",
        paid: false,
        paidAt: "",
        file: null,
        insurer: "",
        product: null,
        isRenewal: false,
        productVariant: null,
        policyYear: "",
        commissionPercent: "",
      },
      rules: {
        required: (v) => !!v || this.$t("policySales.fieldRequired"),
        amount: (v) =>
          /^\d+([.,]\d{1,2})?$/.test(String(v || "").replace(/\s/g, "")) ||
          this.$t("policySales.invalidAmount"),
      },
    };
  },

  computed: {
    headers() {
      return [
        { text: this.$t("policySales.buyerName"), value: "buyerName" },
        { text: this.$t("policySales.policyNumber"), value: "policyNumber" },
        { text: this.$t("policySales.subject"), value: "subject" },
        { text: "Towarzystwo / produkt", value: "insurer", sortable: false },
        { text: this.$t("policySales.amount"), value: "amountGr", sortable: false },
        { text: "Prowizja", value: "commissionGr", sortable: false },
        { text: this.$t("common.status"), value: "paid", sortable: false },
        { text: this.$t("policySales.paidAt"), value: "paidAt", sortable: false },
        { text: this.$t("policySales.file"), value: "file", sortable: false, width: "70px" },
        { text: this.$t("common.date"), value: "createdAt", sortable: false },
        { text: this.$t("common.actions"), value: "actions", sortable: false, width: "110px" },
      ];
    },
    insurerItems() {
      return this.commissionTable.insurers.map((i) => i.name);
    },
    insurerKey() {
      return this.commissionTable.insurers.find((i) => i.name === this.form.insurer)?.key || null;
    },
    selectedProduct() {
      return this.commissionTable.products.find((p) => p.code === this.form.product) || null;
    },
    // Produkty dostępne u wybranego towarzystwa, pogrupowane jak w umowie.
    productItems() {
      const items = [];
      for (const c of this.categoryTabs) {
        const list = this.commissionTable.products.filter(
          (p) => p.category === c.value && (!this.insurerKey || p.insurers.includes(this.insurerKey))
        );
        if (!list.length) continue;
        items.push({ header: c.text });
        items.push(...list);
      }
      return items;
    },
    showRenewal() {
      return !!(this.selectedProduct && this.insurerKey && this.selectedProduct.splitsRenewal.includes(this.insurerKey));
    },
    tableCategory() {
      return this.categoryTabs[this.tableDialog.tab]?.value || 'KOMUNIKACJA';
    },
    tableColumns() {
      const cols = [];
      const seen = new Set();
      for (const r of this.commissionTable.rates) {
        if (r.yearLabel) continue; // życie ma własny układ
        const key = `${r.insurer}|${r.renewal}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const name = this.insurerLabel(r.insurer);
        cols.push({ key, label: r.renewal === null ? name : `${name} ${r.renewal ? 'wzn.' : 'nowe'}` });
      }
      return cols;
    },
    tableRows() {
      const cat = this.tableCategory;
      const products = this.commissionTable.products.filter((p) => p.category === cat);
      if (cat !== 'ZYCIE') {
        return products.map((p) => {
          const cells = {};
          this.commissionTable.rates
            .filter((r) => r.product === p.code)
            .forEach((r) => { cells[`${r.insurer}|${r.renewal}`] = r; });
          return { key: p.code, name: p.name, cells };
        });
      }
      const rows = [];
      for (const p of products) {
        for (const v of p.variants.length ? p.variants : [null]) {
          const rates = this.commissionTable.rates.filter((r) => r.product === p.code && r.variant === v);
          rows.push({
            key: p.code + (v || ''),
            name: p.name,
            insurer: this.insurerLabel(rates[0]?.insurer),
            variant: v,
            years: rates.map((r) => `${r.yearLabel.replace('+', ' i kolejne')}. rok: ${r.text}`).join(' · '),
          });
        }
      }
      return rows;
    },
    paidFilterItems() {
      return [
        { text: this.$t("policySales.filterAll"), value: "" },
        { text: this.$t("policySales.paid"), value: "true" },
        { text: this.$t("policySales.unpaid"), value: "false" },
      ];
    },
    monthFilterLabel() {
      if (!this.monthFilter) return "";
      const [year, month] = this.monthFilter.split("-");
      return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("pl-PL", {
        month: "long",
        year: "numeric",
      });
    },
  },

  watch: {
    form: {
      handler() {
        if (this.formDialog) this.schedulePreview();
      },
      deep: true,
    },
    'form.product'() {
      if (this.selectedProduct && !this.selectedProduct.variants.includes(this.form.productVariant)) {
        this.form.productVariant = null;
      }
    },
    driverSearch(val) {
      if (!val || val.length < 2) return;
      this.searchDrivers(val);
    },
    'offerForm.targetType'(val) {
      if (val === 'FLEET' && !this.carOptions.length) this.loadCars();
    },
    options: {
      handler() {
        this.fetchList();
      },
      deep: true,
    },
    search() {
      this.options.page = 1;
      this.fetchList();
    },
    paidFilter() {
      this.options.page = 1;
      this.fetchList();
    },
    monthFilter() {
      this.options.page = 1;
      this.fetchList();
      this.fetchSummary();
    },
  },

  mounted() {
    this.fetchList();
    this.fetchSummary();
    this.fetchOffers();
    this.fetchCommissionTable();
  },

  methods: {
    // ——— Oferty ———
    async fetchOffers() {
      this.offersLoading = true;
      try {
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'policy-offers', headers: this.getHeaders() });
        this.offers = (res.data?.data || []).map((o) => ({ ...o, createdAt: (o.createdAt || '').slice(0, 10) }));
      } catch (e) { /* lista opcjonalna */ }
      this.offersLoading = false;
    },
    offerTargetLabel(o) {
      if (o.targetType === 'FLEET') return 'Flota BTP';
      const kto = o.driver ? `${o.driver.firstName} ${o.driver.lastName}` : '—';
      return o.targetType === 'SUBFLEET_OWNER' ? `${kto} (podflota)` : kto;
    },
    offerVehicleLabel(o) {
      if (o.car) return `${o.car.make || ''} ${o.car.model || ''} ${o.car.plate || ''}`.trim();
      return o.vehicleDesc || '';
    },
    offerStatusLabel(s) {
      return ({ DRAFT: 'szkic', SENT: 'wysłana', CHOSEN: 'wybrano wariant', ISSUED: 'polisa wystawiona', REJECTED: 'odrzucona', EXPIRED: 'wygasła' })[s] || s;
    },
    offerStatusColor(s) {
      return ({ DRAFT: 'grey', SENT: 'info', CHOSEN: 'amber darken-2', ISSUED: 'success', REJECTED: 'error', EXPIRED: 'blue-grey' })[s] || 'grey';
    },
    chosenVariantLabel(o) {
      const v = (o.variants || []).find((x) => x.id === o.chosenVariantId);
      return v ? `${v.insurer} — ${this.formatAmount(v.premiumGr)}` : '—';
    },
    async searchDrivers(search) {
      try {
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'drivers?skip=1&take=15&search=' + encodeURIComponent(search), headers: this.getHeaders() });
        const rows = res.data?.data?.result || res.data?.data || [];
        this.driverOptions = rows.map((d) => ({ id: d.id, text: `${d.firstName} ${d.lastName}${d.phone ? ' · ' + d.phone : ''}` }));
      } catch (e) { /* ignore */ }
    },
    async loadCars() {
      try {
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'cars?skip=1&take=500', headers: this.getHeaders() });
        const rows = res.data?.data?.result || res.data?.data || [];
        this.carOptions = rows.map((c) => ({ id: c.id, text: `${c.make || ''} ${c.model || ''} ${c.plate || ''}`.trim() }));
      } catch (e) { /* ignore */ }
    },
    openOfferDialog(offer) {
      if (offer) {
        this.offerForm = {
          id: offer.id,
          targetType: offer.targetType,
          driverId: offer.driverId,
          carId: offer.carId,
          vehicleDesc: offer.vehicleDesc || '',
          types: [...(offer.types || [])],
          validUntil: offer.validUntil ? offer.validUntil.slice(0, 10) : '',
          note: offer.note || '',
          variants: (offer.variants || []).map((v) => ({ insurer: v.insurer, scope: v.scope, premiumZl: v.premiumGr / 100, installmentsInfo: v.installmentsInfo || '' })),
        };
        if (offer.driver) this.driverOptions = [{ id: offer.driverId, text: `${offer.driver.firstName} ${offer.driver.lastName}` }];
        if (offer.targetType === 'FLEET') this.loadCars();
      } else {
        this.offerForm = { id: null, targetType: 'DRIVER', driverId: null, carId: null, vehicleDesc: '', types: ['OC'], validUntil: '', note: '', variants: [{ insurer: '', scope: '', premiumZl: null, installmentsInfo: '' }] };
      }
      this.offerDialog = true;
    },
    async saveOffer() {
      this.offerSaving = true;
      const f = this.offerForm;
      try {
        const payload = {
          targetType: f.targetType,
          driverId: f.targetType !== 'FLEET' ? f.driverId : null,
          carId: f.targetType === 'FLEET' ? f.carId : null,
          vehicleDesc: f.vehicleDesc || null,
          types: f.types,
          validUntil: f.validUntil || null,
          note: f.note || null,
          variants: f.variants.map((v) => ({ insurer: v.insurer, scope: v.scope, premiumGr: Math.round((Number(v.premiumZl) || 0) * 100), installmentsInfo: v.installmentsInfo || null })),
        };
        await axios({
          method: f.id ? 'put' : 'post',
          url: process.env.VUE_APP_ROOT_API + 'policy-offers' + (f.id ? '/' + f.id : ''),
          headers: this.getHeaders(),
          data: payload,
        });
        this.offerDialog = false;
        this.fetchOffers();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zapisać oferty', 'error');
      }
      this.offerSaving = false;
    },
    async sendOffer(offer) {
      try {
        await axios({ method: 'post', url: process.env.VUE_APP_ROOT_API + 'policy-offers/' + offer.id + '/send', headers: this.getHeaders() });
        Swal.fire({ icon: 'success', title: 'Oferta wysłana', text: 'Odbiorca dostał SMS i e-mail z wariantami.', timer: 2500, showConfirmButton: false });
        this.fetchOffers();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się wysłać', 'error');
      }
    },
    openChooseDialog(offer) {
      this.chooseDialog = { show: true, offer, variantId: offer.chosenVariantId || null, saving: false };
    },
    async chooseVariant() {
      this.chooseDialog.saving = true;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'policy-offers/' + this.chooseDialog.offer.id + '/choose',
          headers: this.getHeaders(),
          data: { variantId: this.chooseDialog.variantId },
        });
        this.chooseDialog.show = false;
        this.fetchOffers();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zapisać wyboru', 'error');
      }
      this.chooseDialog.saving = false;
    },
    openIssueDialog(offer) {
      this.issueDialog = { show: true, offer, policyNumber: '', startDate: '', endDate: '', saving: false };
    },
    async issuePolicy() {
      this.issueDialog.saving = true;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'policy-offers/' + this.issueDialog.offer.id + '/issue',
          headers: this.getHeaders(),
          data: {
            policyNumber: this.issueDialog.policyNumber,
            startDate: this.issueDialog.startDate,
            endDate: this.issueDialog.endDate,
          },
        });
        this.issueDialog.show = false;
        Swal.fire({ icon: 'success', title: 'Polisa wystawiona', text: 'Dodano do rejestru z prowizją. Dograj skan w edycji pozycji.', timer: 3000, showConfirmButton: false });
        this.fetchOffers();
        this.fetchList();
        this.fetchSummary();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się wystawić polisy', 'error');
      }
      this.issueDialog.saving = false;
    },
    // ——— Tabela prowizji Eurofinance ———
    async fetchCommissionTable() {
      try {
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'policy-sales/commission-table', headers: this.getHeaders() });
        this.commissionTable = res.data.data;
      } catch (e) { /* formularz działa i bez tabeli — stawka ręczna */ }
    },
    async openCommissionTable() {
      if (!this.commissionTable.rates.length) await this.fetchCommissionTable();
      this.tableDialog.show = true;
    },
    insurerLabel(key) {
      return this.commissionTable.insurers.find((i) => i.key === key)?.name || key || '—';
    },
    productLabel(item) {
      const product = this.commissionTable.products.find((p) => p.code === item.product);
      if (!product) return '';
      return [
        product.name,
        item.productVariant,
        item.policyYear ? `${item.policyYear}. rok` : null,
        item.isRenewal ? 'wznowienie' : null,
      ].filter(Boolean).join(' · ');
    },
    formatPercent(p) {
      return `${String(p).replace('.', ',')}%`;
    },
    cellClass(cell) {
      if (!cell) return '';
      return ({ NOT_AVAILABLE: 'cell-na', RANGE: 'cell-range', INDIVIDUAL: 'cell-ind' })[cell.status] || '';
    },
    schedulePreview() {
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(this.fetchPreview, 300);
    },
    // Podgląd liczy backend — ta sama funkcja co przy zapisie polisy.
    async fetchPreview() {
      const f = this.form;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'policy-sales/commission-preview',
          headers: this.getHeaders(),
          params: {
            amount: f.amount || '',
            insurer: f.insurer || '',
            product: f.product || '',
            isRenewal: String(!!f.isRenewal),
            productVariant: f.productVariant || '',
            policyYear: f.policyYear || '',
            commissionPercent: f.commissionPercent || '',
          },
        });
        this.preview = res.data.data;
      } catch (e) {
        this.preview = {
          commissionPercent: null,
          commissionGr: 0,
          commissionManual: false,
          commissionNote: e.response?.data?.message?.toString() || 'Nie udało się policzyć prowizji',
        };
      }
    },

    getHeaders() {
      return {
        Authorization: "Bearer " + localStorage.getItem("token"),
        "x-current-page": "policy-sales",
      };
    },

    formatAmount(gr) {
      return (
        ((gr || 0) / 100).toLocaleString("pl-PL", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + " zł"
      );
    },

    formatDate(d) {
      return d ? new Date(d).toLocaleDateString("pl-PL") : "—";
    },

    async fetchList() {
      this.loading = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "policy-sales",
          headers: this.getHeaders(),
          params: {
            take: this.options.itemsPerPage,
            skip: this.options.page,
            search: this.search || "",
            paid: this.paidFilter || undefined,
            month: this.monthFilter || undefined,
          },
        });
        this.items = res.data.data.result;
        this.total = res.data.data.count;
      } catch (e) {
        Swal.fire(this.$t("common.error"), e.response?.data?.message || this.$t("policySales.loadFailed"), "error");
      } finally {
        this.loading = false;
      }
    },

    async fetchSummary() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "policy-sales/summary",
          headers: this.getHeaders(),
          params: { month: this.monthFilter || undefined },
        });
        this.summary = res.data.data;
      } catch (e) {
        /* podsumowanie nie blokuje strony */
      }
    },

    refresh() {
      this.fetchList();
      this.fetchSummary();
    },

    openCreateDialog() {
      this.editMode = false;
      this.editId = null;
      this.editFileName = null;
      this.form = {
        buyerName: "", policyNumber: "", subject: "", amount: "", paid: false, paidAt: "", file: null,
        insurer: "", product: null, isRenewal: false, productVariant: null, policyYear: "", commissionPercent: "",
      };
      this.formDialog = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },

    openEditDialog(item) {
      this.editMode = true;
      this.editId = item.id;
      this.editFileName = item.originalName || null;
      this.form = {
        buyerName: item.buyerName,
        policyNumber: item.policyNumber,
        subject: item.subject,
        amount: ((item.amountGr || 0) / 100).toFixed(2),
        paid: item.paid,
        paidAt: item.paidAt ? item.paidAt.substring(0, 10) : "",
        file: null,
        insurer: item.insurer || "",
        product: item.product || null,
        isRenewal: !!item.isRenewal,
        productVariant: item.productVariant || null,
        policyYear: item.policyYear ? String(item.policyYear) : "",
        commissionPercent:
          item.commissionManual && item.commissionPercent !== null ? String(item.commissionPercent).replace(".", ",") : "",
      };
      this.formDialog = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },

    async submitForm() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        const formData = new FormData();
        formData.append("buyerName", this.form.buyerName);
        formData.append("policyNumber", this.form.policyNumber);
        formData.append("subject", this.form.subject);
        formData.append("amount", this.form.amount);
        formData.append("paid", String(this.form.paid));
        if (this.form.paid && this.form.paidAt) formData.append("paidAt", this.form.paidAt);
        // Puste wartości czyszczą pole — FormData zamieniłby null na tekst "null".
        formData.append("insurer", this.form.insurer || "");
        formData.append("product", this.form.product || "");
        formData.append("isRenewal", String(!!this.form.isRenewal));
        formData.append("productVariant", this.form.productVariant || "");
        formData.append("policyYear", this.form.policyYear || "");
        formData.append("commissionPercent", this.form.commissionPercent || "");
        if (this.form.file) formData.append("file", this.form.file, this.form.file.name);

        await axios({
          method: this.editMode ? "patch" : "post",
          url: process.env.VUE_APP_ROOT_API + "policy-sales" + (this.editMode ? "/" + this.editId : ""),
          headers: { ...this.getHeaders(), "Content-Type": "multipart/form-data" },
          data: formData,
        });

        Swal.fire(
          this.$t("common.success"),
          this.editMode ? this.$t("policySales.updated") : this.$t("policySales.added"),
          "success"
        );
        this.formDialog = false;
        this.refresh();
      } catch (e) {
        Swal.fire(this.$t("common.error"), e.response?.data?.message || this.$t("policySales.saveFailed"), "error");
      } finally {
        this.saving = false;
      }
    },

    async togglePaid(item) {
      const marking = !item.paid;
      const r = await Swal.fire({
        title: marking ? this.$t("policySales.markPaidTitle") : this.$t("policySales.markUnpaidTitle"),
        html: `<b>${item.policyNumber}</b> — ${item.buyerName}`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("common.yes"),
        cancelButtonText: this.$t("common.cancel"),
      });
      if (!r.isConfirmed) return;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "policy-sales/" + item.id + "/paid",
          headers: this.getHeaders(),
          data: { paid: marking },
        });
        this.refresh();
      } catch (e) {
        Swal.fire(this.$t("common.error"), e.response?.data?.message || this.$t("policySales.saveFailed"), "error");
      }
    },

    async downloadFile(item) {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "policy-sales/" + item.id + "/file",
          headers: this.getHeaders(),
          responseType: "blob",
        });
        const blob = new Blob([res.data], { type: res.headers["content-type"] });
        window.open(URL.createObjectURL(blob), "_blank");
      } catch (e) {
        Swal.fire(this.$t("common.error"), this.$t("policySales.downloadFailed"), "error");
      }
    },

    async confirmDelete(item) {
      const r = await Swal.fire({
        title: this.$t("policySales.deleteTitle"),
        html: `<b>${item.policyNumber}</b> — ${item.buyerName}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: this.$t("common.delete"),
        cancelButtonText: this.$t("common.cancel"),
      });
      if (!r.isConfirmed) return;
      try {
        await axios({
          method: "delete",
          url: process.env.VUE_APP_ROOT_API + "policy-sales/" + item.id,
          headers: this.getHeaders(),
        });
        Swal.fire(this.$t("common.success"), this.$t("policySales.deleted"), "success");
        this.refresh();
      } catch (e) {
        Swal.fire(this.$t("common.error"), e.response?.data?.message || this.$t("policySales.deleteFailed"), "error");
      }
    },
  },
};
</script>

<style scoped>
.policysales-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { padding: 4px 4px 18px; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.summary-card { background: #232838; border: 1px solid rgba(255,255,255,0.06); border-radius: 18px; padding: 16px 20px; }
.summary-label { font-size: 12px; color: #6E7691; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
.summary-value { font-size: 24px; font-weight: 800; margin: 4px 0 2px; }
.summary-sub { font-size: 12px; color: #B0BEC5; }
.accent-blue { color: #5478FF; }
.accent-green { color: #4CAF50; }
.accent-orange { color: #FB8C00; }
.accent-purple { color: #B388FF; }
.summary-warn { font-size: 12px; color: #FFB74D; margin-top: 2px; }
.form-section-label { font-size: 12px; color: #6E7691; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; margin-top: 4px; }
.commission-preview { background: #181C28; border-radius: 12px; padding: 10px 14px; color: #fff; }
.commission-preview--warn { color: #FFB74D; }
.commission-preview-note { font-size: 12px; color: #B0BEC5; margin-top: 2px; }
.commission-table-wrap { overflow-x: auto; }
.commission-table { border-collapse: collapse; width: 100%; font-size: 12px; color: #fff; }
.commission-table th, .commission-table td { border: 1px solid rgba(255,255,255,0.08); padding: 4px 6px; text-align: center; white-space: nowrap; }
.commission-table th { background: #1F2434; color: #B0BEC5; font-weight: 700; position: sticky; top: 0; }
.commission-table td:first-child, .commission-table th:first-child { text-align: left; white-space: normal; min-width: 220px; }
.commission-table td.text-left { text-align: left; white-space: normal; }
.commission-table .cell-na { color: #5C6378; }
.commission-table .cell-range { color: #FFB74D; }
.commission-table .cell-ind { color: #81D4FA; font-style: italic; }
.current-file { font-size: 13px; color: #B0BEC5; margin-top: -8px; }
.current-file-hint { color: #6E7691; }
.policysales-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.policysales-page >>> .v-card .v-card__title { color: #fff !important; padding: 16px 20px 8px !important; font-weight: 700 !important; font-size: 15px !important; }
.policysales-page >>> .v-text-field--outlined fieldset, .policysales-page >>> .v-select--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.policysales-page >>> .v-text-field--outlined .v-input__slot, .policysales-page >>> .v-select--outlined .v-input__slot { background: #181C28 !important; border-radius: 12px !important; }
.policysales-page >>> .v-label { color: #6E7691 !important; }
.policysales-page >>> .v-text-field input, .policysales-page >>> .v-select__selection { color: #fff !important; }
.policysales-page >>> .v-data-table { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; color: #fff !important; box-shadow: none !important; }
.policysales-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.policysales-page >>> .v-data-table tbody tr { background: #232838 !important; }
.policysales-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.policysales-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.policysales-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.policysales-page >>> .v-toolbar { background: transparent !important; color: #fff !important; }
.policysales-page >>> .v-btn:not(.v-btn--icon) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
.policysales-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.policysales-page >>> .v-dialog .v-card { background: #232838 !important; }
</style>
